import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const commonFields = [
  { name: 'full_name', label: 'Full Name', type: 'text' },
  { name: 'mobile', label: 'Mobile Number', type: 'text' },
  { name: 'address', label: 'Address', type: 'text' },
  { name: 'pan_no', label: 'PAN Number', type: 'text' },
  { name: 'aadhar_no', label: 'Aadhar Number', type: 'text' },
  { name: 'gst_credentials', label: 'GST Credentials', type: 'text' }
];

const commonUploads = [
  { name: 'pan_card', label: 'PAN Card Copy' },
  { name: 'aadhar_card', label: 'Aadhar Card Copy' },
  { name: 'itr_2024_25', label: 'ITR File A.Y. 2024-25' },
  { name: 'itr_2025_26', label: 'ITR File A.Y. 2025-26' },
  { name: 'itr_2026_27', label: 'ITR File A.Y. 2026-27' },
  { name: 'itr_2027_28', label: 'ITR File A.Y. 2027-28' },
  { name: 'passport_photo', label: 'Passport Size Photo' },
  { name: 'light_bill', label: 'Light Bill / Address Proof' },
  { name: 'business_proof', label: 'Business Proof' }
];

export default function SubCategoryFormEditor({ subService, API_URL, onUpdate }) {
  const [masterFields, setMasterFields] = useState([]);
  const [masterUploads, setMasterUploads] = useState([]);
  
  const [activeFields, setActiveFields] = useState(subService.active_fields || []);
  const [requiredUploads, setRequiredUploads] = useState(subService.required_uploads || []);
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/form-master`);
        setMasterFields(res.data.fields || []);
        setMasterUploads(res.data.uploads || []);
      } catch (err) {
        toast.error('Failed to load Form Master data');
      }
    };
    fetchMasterData();
  }, [API_URL]);

  const toggleField = (fieldConf) => {
    if (activeFields.find(f => f.name === fieldConf.name)) {
      setActiveFields(activeFields.filter(f => f.name !== fieldConf.name));
    } else {
      setActiveFields([...activeFields, { ...fieldConf, required: true }]);
    }
  };

  const toggleFieldRequired = (fieldName) => {
    setActiveFields((activeFields || []).map(f => 
      f.name === fieldName ? { ...f, required: !f.required } : f
    ));
  };

  const toggleUpload = (uploadConf) => {
    if (requiredUploads.find(u => u.name === uploadConf.name)) {
      setRequiredUploads(requiredUploads.filter(u => u.name !== uploadConf.name));
    } else {
      setRequiredUploads([...requiredUploads, { ...uploadConf, required: true }]);
    }
  };

  const toggleUploadRequired = (uploadName) => {
    setRequiredUploads((requiredUploads || []).map(u => 
      u.name === uploadName ? { ...u, required: !u.required } : u
    ));
  };

  const saveFormConfig = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API_URL}/api/subservices/${subService.id}/form`, {
        active_fields: activeFields,
        required_uploads: requiredUploads
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Form configuration saved successfully!');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to save form configuration');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] p-4 rounded-md mt-3 w-full">
      <h4 className="font-bold text-[#172554] border-b pb-2 mb-4">Dynamic Form Config: {subService.title}</h4>
      
      <div className="flex flex-col md:flex-row gap-8">
        
        {/* Fields configuration */}
        <div className="flex-1">
          <h5 className="font-bold text-sm text-[#64748B] mb-3">1. Active Fields</h5>
          <div className="flex flex-col gap-2">
            {(masterFields || []).map(field => {
              const isActive = !!activeFields.find(f => f.name === field.name);
              const activeField = activeFields.find(f => f.name === field.name);
              return (
                <div key={field.name} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 text-[#F5B800]" checked={isActive} onChange={() => toggleField(field)} />
                    <span className="text-sm font-medium">{field.label}</span>
                  </label>
                  {isActive && (
                    <label className="flex items-center gap-2 text-xs">
                      <span>Required:</span>
                      <div className={`relative inline-block w-8 h-4 cursor-pointer rounded-full transition-colors ${activeField.required ? 'bg-blue-600' : 'bg-gray-300'}`} onClick={() => toggleFieldRequired(field.name)}>
                        <span className={`absolute top-0.5 left-0.5 bg-white w-3 h-3 rounded-full transition-transform ${activeField.required ? 'translate-x-4' : 'translate-x-0'}`}></span>
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Uploads configuration */}
        <div className="flex-1">
          <h5 className="font-bold text-sm text-[#64748B] mb-3">2. Required Document Uploads</h5>
          <div className="flex flex-col gap-2">
            {(masterUploads || []).map(upload => {
              const isActive = !!requiredUploads.find(u => u.name === upload.name);
              const activeUpload = requiredUploads.find(u => u.name === upload.name);
              // Visual grouping styling for ITR files
              const isITR = upload.name.startsWith('itr_');
              return (
                <div key={upload.name} className={`flex items-center justify-between p-2 rounded border ${isITR ? 'bg-blue-50/30 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 text-[#F5B800]" checked={isActive} onChange={() => toggleUpload(upload)} />
                    <span className={`text-sm font-medium ${isITR ? 'text-blue-800' : ''}`}>{upload.label}</span>
                  </label>
                  {isActive && (
                    <label className="flex items-center gap-2 text-xs">
                      <span>Required:</span>
                      <div className={`relative inline-block w-8 h-4 cursor-pointer rounded-full transition-colors ${activeUpload.required ? 'bg-blue-600' : 'bg-gray-300'}`} onClick={() => toggleUploadRequired(upload.name)}>
                        <span className={`absolute top-0.5 left-0.5 bg-white w-3 h-3 rounded-full transition-transform ${activeUpload.required ? 'translate-x-4' : 'translate-x-0'}`}></span>
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button 
          onClick={saveFormConfig}
          disabled={isSaving}
          className="bg-[#172554] text-white px-6 py-2 rounded font-medium hover:bg-blue-900 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Form Configuration'}
        </button>
      </div>
    </div>
  );
}